import { MaintenanceRequest } from "@/types/types";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MaintenanceStatusBadge, PriorityBadge } from "@/lib/maintenance-utils";
import { formatDate } from "@/lib/date";
import { formatNaira } from "@/lib/utils";

interface MaintenanceDetailsDialogProps {
  request: MaintenanceRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MaintenanceDetailsDialog({
  request,
  open,
  onOpenChange,
}: MaintenanceDetailsDialogProps) {
  if (!request) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>{request.title}</AlertDialogTitle>
          <AlertDialogDescription>
            Request details and status information
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Status
              </p>
              <MaintenanceStatusBadge status={request.status} />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Priority
              </p>
              <PriorityBadge priority={request.priority} />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Category
              </p>
              <p className="capitalize">{request.category}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Date Submitted
              </p>
              <p>{formatDate(request.dateSubmitted)}</p>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground">
              Description
            </p>
            <p className="text-sm mt-1">{request.description}</p>
          </div>

          {/* Display uploaded images/videos */}
          {request.images && request.images.length > 0 && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Attachments
              </p>
              <div className="grid grid-cols-2 gap-3">
                {request.images.map((url, index) => {
                  // Check if it's a video by URL extension or content type
                  const isVideo = url.match(/\.(mp4|webm|ogg|mov)(\?|$)/i);

                  return (
                    <div
                      key={index}
                      className="relative rounded-lg overflow-hidden border"
                    >
                      {isVideo ? (
                        <video
                          src={url}
                          controls
                          className="w-full h-48 object-cover"
                        />
                      ) : (
                        <img
                          src={url}
                          alt={`Attachment ${index + 1}`}
                          className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(url, "_blank")}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {request.assignedTo && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Assigned To
                </p>
                <p className="text-sm">{request.assignedTo}</p>
              </div>
              {request.assignedPhone && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Contact
                  </p>
                  <p className="text-sm">{request.assignedPhone}</p>
                </div>
              )}
            </div>
          )}

          {request.scheduledDate && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Scheduled Date
              </p>
              <p className="text-sm">{formatDate(request.scheduledDate)}</p>
            </div>
          )}

          {request.actualCost && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Cost</p>
              <p className="text-sm">{formatNaira(request.actualCost)}</p>
            </div>
          )}

          {request.notes && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Notes</p>
              <p className="text-sm mt-1">{request.notes}</p>
            </div>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
